import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import SEO from './components/SEO';
import { ThemeContext } from './components/Core/ThemeContext';
import { perspectives } from './data/perspectives';
import { perspectiveMatchesQuery } from './utils/perspectiveSearch';
import { getPerspectiveReviewStage, PERSPECTIVE_REVIEW_STAGES } from './utils/perspectiveReviewStages';
import './perspectives.css';

function PagePerspectivesHub() {
    const theme = useContext(ThemeContext);
    const [query, setQuery] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
        theme.loading(false);
    }, [theme]);

    const title = 'Bible Perspectives | Compare Major Interpretations';
    const description = 'Explore carefully sourced, AI-assisted guides to major questions in biblical interpretation, comparing distinct perspectives without hiding their differences.';
    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Bible Perspectives',
        description,
        url: 'https://www.ai-bible.com/perspectives',
        hasPart: perspectives.map((topic) => ({
            '@type': 'Article',
            headline: topic.question,
            url: `https://www.ai-bible.com/perspectives/${topic.slug}`,
        })),
    };
    const filteredPerspectives = useMemo(() => {
        return perspectives.filter((topic) => perspectiveMatchesQuery(topic, query));
    }, [query]);

    return (
        <main className="perspectives-page perspectives-hub">
            <SEO canonical="/perspectives" title={title} description={description} structuredData={structuredData} />
            <div className="perspectives-frame">
                <header className="perspectives-hero">
                    <span className="perspectives-hero-strip" aria-hidden="true" />
                    <div className="perspectives-hero-copy">
                        <p className="perspectives-eyebrow">Interpretation in conversation</p>
                        <h1>Perspectives</h1>
                        <p>
                            Compare major readings of difficult biblical questions, see who is associated with each view, and follow the sources behind the summary.
                        </p>
                    </div>
                </header>

                <aside className="perspectives-ai-note" aria-label="AI content notice">
                    <span className="perspectives-ai-icon" aria-hidden="true">✦</span>
                    <div className="perspectives-ai-note-content">
                        <div>
                            <strong>AI-generated research</strong>
                            <span> These guides organize cited interpretations for comparison. Human review status is shown on every page.</span>
                        </div>
                        <details className="perspectives-review-guide">
                            <summary>How review stages progress</summary>
                            <ol>
                                {PERSPECTIVE_REVIEW_STAGES.map((stage) => (
                                    <li key={stage.id}>
                                        <span className={`perspective-review-status perspective-review-stage-${stage.id}`}>
                                            {stage.id === 'graduate-citations-checked' ? 'Graduate Reviewed' : stage.id === 'expert-reviewed' ? 'Expert Reviewed' : stage.status}
                                        </span>
                                        <p>{stage.explanation}</p>
                                    </li>
                                ))}
                            </ol>
                        </details>
                    </div>
                </aside>

                <div className="perspectives-search">
                    <label htmlFor="perspectives-search-input">Search perspectives</label>
                    <div className="perspectives-search-field">
                        <span aria-hidden="true">⌕</span>
                        <input
                            id="perspectives-search-input"
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Search titles or related terms, e.g. community for fellowship"
                        />
                        {query ? <button type="button" onClick={() => setQuery('')}>Clear</button> : null}
                    </div>
                </div>

                <section aria-label="Perspective topics">
                    <div className="perspectives-results-bar">
                        <span>{filteredPerspectives.length} of {perspectives.length} topic{perspectives.length === 1 ? '' : 's'}</span>
                        <Link className="perspectives-map-link" to="/perspectives/chart">Browse the relationship map <span aria-hidden="true">→</span></Link>
                    </div>

                    <div className="perspectives-topic-grid">
                        {filteredPerspectives.map((topic) => {
                            const reviewStage = getPerspectiveReviewStage(topic.generation.status);
                            return (
                                <article className="perspectives-topic-card" key={topic.slug}>
                                    <span className="perspectives-topic-strip" aria-hidden="true" />
                                    <div className="perspectives-topic-content">
                                        <p className="perspectives-topic-category">{topic.category}</p>
                                        <h3>{topic.question}</h3>
                                        <p>{topic.summary}</p>
                                        <div className="perspectives-topic-meta">
                                            <span>{topic.readings.length} perspectives</span>
                                            <span>{topic.sources.length} sources</span>
                                            <span className={`perspective-review-status perspective-review-stage-${reviewStage.id}`} title={reviewStage.explanation}>{reviewStage.status}</span>
                                        </div>
                                        <Link className="perspectives-topic-link" to={`/perspectives/${topic.slug}`}>
                                            Compare perspectives
                                            <ArrowForwardIosIcon aria-hidden="true" />
                                        </Link>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                    {!filteredPerspectives.length ? (
                        <div className="perspectives-search-empty">
                            <h2>No matching perspectives</h2>
                            <p>Try a broader question, biblical topic, or tradition.</p>
                            <button type="button" onClick={() => setQuery('')}>Clear search</button>
                        </div>
                    ) : null}
                </section>
            </div>
        </main>
    );
}

export default PagePerspectivesHub;
